import { useQuery } from "@tanstack/react-query";
import type { Me } from "@owners/shared-types";

export class ApiError extends Error {
  constructor(public status: number, message: string) { super(message); }
}

/** fetch wrapper: same-origin cookies, JSON, and the header the API requires on writes. */
export async function api<T = any>(path: string, init: { method?: string; body?: unknown } = {}): Promise<T> {
  const r = await fetch(path, {
    method: init.method ?? "GET",
    credentials: "same-origin",
    headers: { "x-owners-app": "1", ...(init.body !== undefined ? { "content-type": "application/json" } : {}) },
    body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
  });
  const data = r.headers.get("content-type")?.includes("json") ? await r.json() : null;
  if (!r.ok) throw new ApiError(r.status, data?.error ?? `Request failed (${r.status})`);
  return data as T;
}

/** POST JSON, save the binary response as a file (e.g. the Select downloads zip). */
export async function downloadPost(path: string, body: unknown, fallbackName: string): Promise<void> {
  const r = await fetch(path, { method: "POST", credentials: "same-origin",
    headers: { "x-owners-app": "1", "content-type": "application/json" }, body: JSON.stringify(body) });
  if (!r.ok) {
    const data = r.headers.get("content-type")?.includes("json") ? await r.json() : null;
    throw new ApiError(r.status, data?.error ?? `Download failed (${r.status})`);
  }
  const cd = r.headers.get("content-disposition") ?? "";
  const m = /filename\*=UTF-8''([^;]+)/.exec(cd);
  const url = URL.createObjectURL(await r.blob());
  const a = document.createElement("a");
  a.href = url; a.download = m ? decodeURIComponent(m[1]!) : fallbackName;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export function useMe() {
  return useQuery<Me | null>({
    queryKey: ["me"],
    queryFn: async () => {
      try {
        return await api<Me>("/api/me");
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 60_000,
  });
}

export function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join("");
}

/** Settings → Default views / Reports & delivery (GET/PUT /api/me/preferences). Absent key = the built-in default. */
export interface Prefs {
  home?: import("@owners/shared-types").PageKey;
  flashPeriod?: "yesterday" | "last_week" | "last_month" | "ytd" | "t12";
  pnlView?: "current_month" | "last_month" | "ytd" | "prior_year_end";
  reportFormat?: "pdf" | "xlsx" | "both";
}
export function usePrefs() {
  return useQuery({ queryKey: ["prefs"], queryFn: async () => (await api<{ prefs: Prefs }>("/api/me/preferences")).prefs, staleTime: Infinity });
}
